import { useEffect, useState } from 'react';
import { withResolvers } from '~/utils/promises';

type Timer = ReturnType<(typeof globalThis)['setTimeout']>;

export function debounce<Args extends any[]>(fn: (...args: Args) => void, delay = 100) {
  if (delay === 0) {
    return fn;
  }

  let timer: Timer | undefined;

  return function <U>(this: U, ...args: Args) {
    const context = this;

    globalThis.clearTimeout(timer);

    timer = globalThis.setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
}

export interface ThrottleOptions {
  leading?: boolean;
}

export function throttle<Args extends any[]>(
  fn: (...args: Args) => void,
  delay = 100,
  { leading = false }: ThrottleOptions = {},
) {
  if (delay === 0) {
    return fn;
  }

  let timer: Timer | undefined;

  return function <U>(this: U, ...args: Args) {
    const context = this;

    if (timer !== undefined) {
      return;
    }

    if (leading) {
      fn.apply(context, args);
    }

    timer = globalThis.setTimeout(() => {
      timer = undefined;
      fn.apply(context, args);
    }, delay);
  };
}

type AsyncDebouncedCallback<Args extends any[]> = <U>(this: U, ...args: Args) => Promise<void>;

/**
 * Similar to `debounce` but for asynchronous functions where the all calls to the debounced
 * function will returns a promise that completes when the underlying async operation completes.
 *
 * In addition, `debounceAndQueue` does a bit more: it queues the calls to the async function and
 * attempts to cancel any existing one.
 *
 * The rationale for the queueing is to avoid surprises where we want to mimic how a traditional
 * debounce operation on a given series of events over time, will remove some of those events but
 * ALWAYS return them in order.
 *
 * What this practically means for promises returned from the debounced function:
 *
 *    The returned promises will ALWAYS complete in order that they were returned.
 *
 * Finally worth noting that the cancellation is completely optional and the provided function
 * can ignore it entirely.
 *
 * The cancellation works with an `AbortSignal` as this can be used with `waitUntil` or `fetch`.
 *
 *
 * Let's look at an example:
 *
 * ```ts
 * const debouncedFn = debounceAndQueue((signal: AbortSignal) => {
 *   await wait(100);
 *
 *   signal.throwIfAborted();
 *
 *   console.log('foobar');
 * }, 10);
 *
 * // we now call the debounced function 3 times.
 * const promise1 = debouncedFn();
 * const promise2 = debouncedFn();
 *
 * setTimeout(() => {
 *   const promise3 = debouncedFn();
 * }, 40)
 *
 * // expected outcome:
 * //  - the three promises will complete at the same time
 * //  - a single call to console.log will happen
 * //  - the debounced callback was invoked twice but accepted the cancellation with `signal.throwIfAborted()`
 * ```
 *
 * @param fn Asyncronous function to invoke with the extra abortSignal
 * @param delay Delay to use
 */
export function debounceAndQueue<Args extends any[]>(
  fn: <U>(this: U, signal: AbortSignal, ...args: Args) => Promise<void>,
  delay = 100,
): AsyncDebouncedCallback<Args> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  let previousController: AbortController | undefined;

  let previous:
    | {
        resolvers: PromiseWithResolvers<void>;
        cancellation: PromiseWithResolvers<void>;
      }
    | undefined;
  let current:
    | {
        resolvers: PromiseWithResolvers<void>;
        cancellation: PromiseWithResolvers<void>;
      }
    | undefined;

  return function <U>(this: U, ...args: Args) {
    const context = this;

    clearTimeout(timer);

    let resolvers: PromiseWithResolvers<void>;
    let cancellation: PromiseWithResolvers<void>;

    /**
     * If the previous controller was set it means a previous task already started,
     * we abort it and then reset `previousController` to undefined.
     *
     * We also create a new resolvers because the previous resolvers are going to
     * maybe get called by the previous task running (if it ignored the abort event)
     */
    if (previousController) {
      previousController.abort(DEBOUNCE_ABORT);
      previousController = undefined;

      resolvers = withResolvers<void>();
      cancellation = withResolvers<void>();

      previous = current;
    } else {
      resolvers = current?.resolvers ?? withResolvers<void>();
      cancellation = current?.cancellation ?? withResolvers<void>();
    }

    current = { resolvers, cancellation };

    const capturedPrevious = previous;

    timer = setTimeout(() => {
      previousController = new AbortController();

      const signal = previousController.signal;

      const onResolve = () => {
        capturedPrevious?.resolvers.resolve();
        resolvers.resolve();
      };
      const onReject = (error: unknown) => {
        if (error === DEBOUNCE_ABORT) {
          cancellation.resolve();

          return;
        }

        capturedPrevious?.resolvers.reject(error);
        resolvers.reject(error);
      };

      const invoke = () => fn.call(context, signal, ...args).then(onResolve, onReject);

      Promise.race([capturedPrevious?.resolvers.promise, capturedPrevious?.cancellation.promise]).then(invoke, invoke);
    }, delay);

    return resolvers.promise;
  };
}

export class DebounceAbortError extends Error {}

const DEBOUNCE_ABORT = new DebounceAbortError();

/**
 * Debounce updates of a value, similar to VueUse's refDebounced.
 *
 * The debounced value will lag behind the input value by the specified delay.
 * This is useful for expensive operations like API calls or search queries
 * that should only trigger after the user has stopped typing.
 *
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 200ms)
 * @returns The debounced value
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
 *
 * // API call only triggers when user stops typing for 300ms
 * useEffect(() => {
 *   if (debouncedSearchTerm) {
 *     searchAPI(debouncedSearchTerm);
 *   }
 * }, [debouncedSearchTerm]);
 * ```
 */
export function useDebouncedValue<T>(value: T, delay: number = 200): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}
